import { Link, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, Download } from "lucide-react"
import { api, ApiError, type VideoDetailResponse } from "@/lib/types"
import { formatDate, formatDuration } from "@/lib/format"
import { TagChip } from "@lilbillbiscuit/account-ui"

export function VideoPage() {
  const { slug = "" } = useParams()
  const q = useQuery<VideoDetailResponse, ApiError>({
    queryKey: ["video", slug],
    queryFn: () => api<VideoDetailResponse>(`/api/videos/${encodeURIComponent(slug)}`),
    retry: false,
  })

  if (q.isLoading) return <div className="py-12 text-center text-sm text-th-fg/50">Loading video…</div>
  if (q.error) {
    return (
      <div className="glass-section text-center">
        <h1 className="font-heading text-2xl">Video not found</h1>
        <p className="mt-2 text-sm text-th-fg/60">{q.error.serverMessage ?? "We couldn't load this concert."}</p>
        <Link to="/" className="btn mt-6"><ArrowLeft size={14} /> Back to catalog</Link>
      </div>
    )
  }
  const { video, playback } = q.data!

  return (
    <div className="space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-th-fg/60 hover:text-th-accent">
        <ArrowLeft size={14} /> Back to catalog
      </Link>
      <div className="aspect-video w-full overflow-hidden rounded-2xl border border-th-glass/[0.08] bg-black">
        <Player playback={playback} title={video.title} />
      </div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-3xl font-semibold leading-tight">{video.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-th-fg/60">
            <span>{formatDate(video.performedOn)}</span>
            {video.durationSeconds && <span>· {formatDuration(video.durationSeconds)}</span>}
            <span>· {video.sourceType}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {video.tags.map((s) => <TagChip key={s} slug={s} />)}
          </div>
        </div>
        {playback.downloadUrl && (
          <a
            href={playback.downloadUrl}
            // The browser honors `download` only for same-origin URLs; for cross-
            // origin (YouTube/GDrive/OneDrive) the attribute is a hint and the
            // server's Content-Disposition wins. For SharePoint shares the URL
            // delivers Content-Type: video/mp4 which most browsers will save.
            download={`${video.slug}.mp4`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            <Download size={14} /> Download
          </a>
        )}
      </header>
      {video.description && (
        <div className="glass-card text-sm leading-relaxed text-th-fg/80 whitespace-pre-wrap">
          {video.description}
        </div>
      )}
    </div>
  )
}

function Player({ playback, title }: { playback: VideoDetailResponse["playback"]; title: string }) {
  if (playback.kind === "r2" && playback.streamUrl) {
    return (
      <video
        src={playback.streamUrl}
        controls
        playsInline
        preload="metadata"
        className="h-full w-full"
      />
    )
  }
  if (playback.embedUrl) {
    return (
      <iframe
        src={playback.embedUrl}
        title={title}
        className="h-full w-full"
        allow={playback.iframeAllow || "autoplay; encrypted-media; picture-in-picture"}
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    )
  }
  return <div className="flex h-full items-center justify-center text-sm text-th-fg/40">This video has no playable source.</div>
}
